/*
 *
 * ManageVasPopup
 *
 * @version 1.0.0
 * @author Christopher Monti
 * @since 3-Mar-2025
 */

import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import MerchantModal from '@nexidigital/merchantmodal'
import { getFeatureParamsValue } from 'Utility/subscriptionMethods'
import useManageVasForm from 'Hooks/useManageVasForm'
import { VAS } from 'Schemas/vas'
import ServiceConfig from '../../ServiceConfig'
import formattedMessage, { captions } from './languageModule'
import './ManageVasPopup.scss'

const ManageVasPopup = ({ data, isUpdatingVas, actions }) => {
  const { vas, info } = data
  const intl = useIntl()
  const vasName = vas?.feature?.name || ''
  const isTKN = vasName === 'NetworkTokenization'
  const isClickToPay = vasName === 'ClickToPay'

  const vasParams = vas?.params || []
  const baseUrlValue = isTKN
    ? getFeatureParamsValue(vas, 'NetworkTokenization', 'TokenizationByCardSchemeBaseUrl')
    : null
  const clickToPayBaseUrlValue = isClickToPay
    ? getFeatureParamsValue(vas, 'ClickToPay', 'ClickToPayBaseUrl')
    : null
  const notificationUrlValue = isTKN
    ? getFeatureParamsValue(vas, 'NetworkTokenization', 'TokenizationByCardSchemeLifecycleNotificationUrl')
    : null
  const isBaseUrlTokenization = isTKN && (!vasParams.length || !baseUrlValue)
  const isNotificationUrlTokenization = isTKN && (!!notificationUrlValue || !!baseUrlValue)
  const isClickToPayBaseUrl = isClickToPay && (!vasParams.length || !clickToPayBaseUrlValue)

  const subscriptionParamsByName = vasParams.reduce((acc, param) => {
    acc[param.featureParam.name] = param.featureParam.id
    return acc
  }, {})

  const subscriptionParamsInitial = vasParams.reduce((acc, param) => {
    acc[param.featureParam.name] = param.value ?? ''
    return acc
  }, {})

  const buildPayload = (values) => ({
    subscriptionParams: Object.entries(values.subscriptionParams || {}).map(([name, value]) => ({
      featureParam: {
        id: subscriptionParamsByName[name] ?? vas?.feature?.id,
        name
      },
      value: value || null
    }))
  })

  const formik = useManageVasForm({
    onSubmit: (values) => {
      const payload = buildPayload(values)
      actions.doPostSubscriptionGlobal({ values, vas, payload })
    },
    initialValues: {
      status: vas?.status || 'Disabled',
      subscriptionParams: subscriptionParamsInitial, // add initial values for params
    },
    validationContext: { isBaseUrlTokenization, isNotificationUrlTokenization, isClickToPayBaseUrl } // pass context to validation Yup schema
  })
  const vasNameMessage = VAS[vasName?.toUpperCase()]?.name

  return (
    <MerchantModal
      closeLabel={intl.formatMessage(captions.close)}
      title={formattedMessage.titleManage}
      onClose={() => actions.closePopup()}
      primaryButtonLabel={formattedMessage.confirm}
      primaryAction={() => formik.submitForm()}
      primaryButtonDisabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
      primaryButtonLoading={isUpdatingVas}>
      <ServiceConfig
        formik={formik}
        title={vasNameMessage ? intl.formatMessage(vasNameMessage) : vasName}
        description={formattedMessage.description[vasName]}
        active={formik.values.status === 'Enabled'}
        onSwitchChange={(selectedOption) => formik.setFieldValue('status', selectedOption ? 'Enabled' : 'Disabled')}
        info={info?.map((item) => ({ ...item, label: formattedMessage.info[item.name] }))}
        vas={vas}
      />
    </MerchantModal>
  )
}

ManageVasPopup.propTypes = {
  actions: PropTypes.object,
  data: PropTypes.object,
  isUpdatingVas: PropTypes.bool
}

ManageVasPopup.defaultProps = {
  actions: {},
  data: {},
  isUpdatingVas: false
}

export default ManageVasPopup
